import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users,
  Building2,
  Calendar,
  TrendingUp,
  Clock,
  UserCheck,
  Phone,
  Mail,
  Cloud,
  Download,
  FolderOpen,
  CheckSquare,
  CreditCard,
  AlertCircle,
} from 'lucide-react';
import { meetingsService, contactsService, activitiesService, agentCallsService, supabase } from '../services/supabaseService';
import { leadsService } from '../services/supabaseService';
import authService from '../services/authService';
import AIInsights from './AIInsights';
import type { AIInsight } from '../types';
import { AgentCall } from '../types/agentCall';
import { useDataSubscription } from '../contexts/DataSyncContext';
import { useDataLoader } from '../hooks/useDataLoader';
import { usePermissions } from '../hooks/usePermissions';
import { truncateText, formatTime, formatDate } from '../utils/textUtils';
import { logger } from '../utils/logger';
import AgentCallsList from './AgentCallsList';
import AgentCallDetailModal from './AgentCallDetailModal';

const AdminDashboard: React.FC = () => {
  const reloadTriggerRef = useRef(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const currentUser = authService.getCurrentSession()?.user;
  const { isAdmin } = usePermissions();
  const userIsAdmin = isAdmin();

  const { data: dashboardData, loading: loadingDashboard, isRevalidating: revalidatingDashboard, refetch: refetchDashboard } = useDataLoader(
    `dashboard-all-${userIsAdmin ? 'admin' : currentUser?.id}`,
    async () => {
      const filterUserId = userIsAdmin ? undefined : currentUser?.name;

      // Dashboard sólo necesita: stats de leads (status + count), pipeline
      // por leadStatus de contactos, y listas top-N de contacts/meetings/
      // activities. Antes traíamos ~40 columnas de cada lead (incluidas
      // conversation_full, lead_analysis_summary, etc) sólo para contarlos.
      const [recentContacts, upcomingMeetings, recentActivities, leadsSnapshot, contactStatuses] = await Promise.all([
        contactsService.getRecent(20),
        meetingsService.getUpcoming(50),
        activitiesService.getRecent(10, filterUserId),
        leadsService.getStatusSnapshot(),
        contactsService.getLeadStatusSnapshot(),
      ]);

      let userMeetings = upcomingMeetings;
      if (!userIsAdmin) {
        userMeetings = upcomingMeetings.filter(meeting => {
          const isAssigned = meeting.assignedUserId === currentUser?.id;
          const isOwner = meeting.owner === currentUser?.name;
          return isAssigned || isOwner;
        });
      }

      const convertedLeads = leadsSnapshot.filter(l => l.status === 'convertido').length;
      const totalLeads = leadsSnapshot.length;
      const conversionRate = totalLeads > 0 ? (convertedLeads / totalLeads) * 100 : 0;
      const activeLeadIds = new Set(
        leadsSnapshot
          .filter(l => l.status === 'nuevo' || l.status === 'contactado' || l.status === 'calificado')
          .map(l => l.id)
      );

      return {
        contacts: recentContacts,
        meetings: userMeetings.slice(0, 10),
        activities: recentActivities,
        // La lista "Leads activos" del dashboard antes eran objetos completos.
        // Ahora sólo tenemos snapshot, así que exponemos count + los IDs para
        // que el componente pueda linkar. Si la vista mostraba nombres, esos
        // se cargarían con getById cuando el usuario haga click.
        leads: leadsSnapshot.filter(l => activeLeadIds.has(l.id)),
        conversionRate,
        pipeline: {
          nuevo: contactStatuses.filter(c => c.leadStatus === 'nuevo').length,
          en_seguimiento: contactStatuses.filter(c => c.leadStatus === 'en_seguimiento').length,
          cerrado: contactStatuses.filter(c => c.leadStatus === 'cerrado').length,
          descartado: contactStatuses.filter(c => c.leadStatus === 'descartado').length,
          ya_es_cliente: contactStatuses.filter(c => c.leadStatus === 'ya_es_cliente').length
        }
      };
    },
    { ttl: 60 }
  );

  const debouncedRefetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      logger.debug('Dashboard: Debounced refetch triggered');
      refetchDashboard();
    }, 500);
  }, [refetchDashboard]);

  useDataSubscription('contacts', () => {
    logger.debug('Dashboard: contacts changed');
    debouncedRefetch();
  });

  useDataSubscription('leads', () => {
    logger.debug('Dashboard: leads changed');
    debouncedRefetch();
  });

  useDataSubscription('meetings', () => {
    logger.debug('Dashboard: meetings changed');
    debouncedRefetch();
  });

  useDataSubscription('activities', () => {
    logger.debug('Dashboard: activities changed');
    debouncedRefetch();
  });

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // -------- Panel de Operaciones (proyectos, tareas, partes, pagos) --------
  const [ops, setOps] = useState<{
    activeProjects: number;
    projectsThisMonth: number;
    openTasks: number;
    tasksDueSoon: number;
    submittedTimesheets: number;
    monthAmount: number;
    pendingPayments: number;
    recentProjects: Array<{ id: string; name: string; client_name: string; status: string }>;
  } | null>(null);

  useEffect(() => {
    if (!userIsAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const in7Days = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const today = now.toISOString().split('T')[0];

        const [projRes, tasksOpenRes, tasksDueRes, submittedRes, timesheetsMonth, pendingPaymentsRes, recentProj] = await Promise.all([
          supabase.from('projects').select('id, created_at, status', { count: 'exact' }).eq('status', 'active'),
          supabase.from('project_tasks').select('id', { count: 'exact', head: true }).in('status', ['backlog', 'in_progress', 'in_review']),
          supabase.from('project_tasks').select('id', { count: 'exact', head: true }).gte('due_date', today).lte('due_date', in7Days).neq('status', 'done'),
          supabase.from('timesheets').select('id', { count: 'exact', head: true }).eq('status', 'submitted'),
          supabase.from('timesheets').select('calculated_amount').eq('status', 'approved').gte('date', monthStart),
          supabase.from('payments').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
          supabase.from('projects').select('id, name, client_name, status').order('created_at', { ascending: false }).limit(4),
        ]);

        const active = projRes.count || 0;
        const projectsThisMonth = (projRes.data || []).filter((p: any) => new Date(p.created_at) >= new Date(monthStart)).length;
        const monthAmount = (timesheetsMonth.data || []).reduce((s: number, r: any) => s + Number(r.calculated_amount || 0), 0);

        if (cancelled) return;
        setOps({
          activeProjects: active,
          projectsThisMonth,
          openTasks: tasksOpenRes.count || 0,
          tasksDueSoon: tasksDueRes.count || 0,
          submittedTimesheets: submittedRes.count || 0,
          monthAmount,
          pendingPayments: pendingPaymentsRes.count || 0,
          recentProjects: recentProj.data || [],
        });
      } catch (err) {
        logger.warn('Dashboard: no se pudo cargar el panel de operaciones', err);
      }
    })();
    return () => { cancelled = true; };
  }, [userIsAdmin]);

  const meetings = dashboardData?.meetings || [];
  const contacts = dashboardData?.contacts || [];
  const activities = dashboardData?.activities || [];
  const leads = dashboardData?.leads || [];
  const conversionRate = dashboardData?.conversionRate || 0;
  const pipelineStats = dashboardData?.pipeline || {};

  const userMeetings = meetings;

  const [recentAgentCalls, setRecentAgentCalls] = useState<AgentCall[]>([]);
  const [recentCallsLoading, setRecentCallsLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState<AgentCall | null>(null);
  const [callModalOpen, setCallModalOpen] = useState(false);

  useEffect(() => {
    if (!loadingDashboard) {
      logger.debug('Dashboard render - meetings', {
        total: meetings.length,
        isAdmin: userIsAdmin,
        data: meetings
      });
    }
  }, [meetings, loadingDashboard, userIsAdmin]);

  useEffect(() => {
    let cancelled = false;
    setRecentCallsLoading(true);

    agentCallsService.getRecent(20)
      .then(calls => {
        if (cancelled) return;
        setRecentAgentCalls(calls);
      })
      .catch(err => {
        console.error('[AGENT_CALLS] Error loading recent calls', err);
      })
      .finally(() => {
        if (!cancelled) setRecentCallsLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!loadingDashboard) {
      logger.debug('Dashboard render - activities', {
        total: activities.length,
        isAdmin: userIsAdmin,
        userName: currentUser?.name,
        data: activities.length > 0 ? activities : 'No activities'
      });
    }
  }, [activities, loadingDashboard, userIsAdmin, currentUser]);

  const isRevalidating = revalidatingDashboard;
  const loadingContacts = loadingDashboard;
  const loadingMeetings = loadingDashboard;
  const loadingActivities = loadingDashboard;
  const loadingLeads = loadingDashboard;

  const totalContacts = Object.values(pipelineStats).reduce((sum, count) => sum + count, 0) || contacts.length;
  const activeLeads = leads.length;
  const upcomingMeetingsCount = userMeetings.length;

  const stats = [
    {
      title: 'Total Contactos',
      value: totalContacts.toString(),
      icon: Users,
      color: 'bg-blue-500',
      change: totalContacts > 0 ? `+${Math.round((totalContacts / Math.max(totalContacts - 1, 1)) * 100 - 100)}%` : '0%',
      changeType: 'positive' as const
    },
    {
      title: 'Leads Activos',
      value: activeLeads.toString(),
      icon: UserCheck,
      color: 'bg-green-500',
      change: activeLeads > 0 ? `+${Math.round(activeLeads / Math.max(activeLeads, 1) * 10)}%` : '0%',
      changeType: 'positive' as const
    },
    {
      title: 'Reuniones Esta Semana',
      value: upcomingMeetingsCount.toString(),
      icon: Calendar,
      color: 'bg-purple-500',
      change: upcomingMeetingsCount > 0 ? `+${upcomingMeetingsCount}` : '0',
      changeType: 'positive' as const
    },
    {
      title: 'Tasa de Conversión',
      value: `${conversionRate.toFixed(1)}%`,
      icon: TrendingUp,
      color: 'bg-orange-500',
      change: conversionRate > 0 ? `+${(conversionRate / 10).toFixed(1)}%` : '0%',
      changeType: 'positive' as const
    }
  ];

  const handleInsightAction = (insight: AIInsight, action: 'accept' | 'dismiss') => {
    console.log(`${action} insight:`, insight);
    if (action === 'accept') {
      switch (insight.type) {
        case 'duplicate_detection':
          break;
        case 'meeting_suggestion':
          break;
        case 'lead_priority':
          break;
      }
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'reunion': return Calendar;
      case 'llamada': return Phone;
      case 'correo': return Mail;
      default: return Clock;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'reunion': return 'text-orange-600 bg-orange-100';
      case 'llamada': return 'text-blue-600 bg-blue-100';
      case 'correo': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
            <p className="text-gray-600">Resumen de actividad comercial y métricas clave</p>
          </div>
          {isRevalidating && (
            <div className="flex items-center space-x-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span>Actualizando datos...</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <div className="flex items-center mt-2">
                  <span className={`text-xs font-medium ${
                    stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change}
                  </span>
                  <span className="text-xs text-gray-500 ml-1">vs mes anterior</span>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Fila 1: AIInsights (izq) + Panel de Operaciones (der) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <AIInsights
            contacts={contacts}
            activities={activities}
            onInsightAction={handleInsightAction}
          />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">Operaciones</h2>
            {ops?.submittedTimesheets ? (
              <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                {ops.submittedTimesheets} pendiente{ops.submittedTimesheets !== 1 ? 's' : ''}
              </span>
            ) : null}
          </div>

          {ops ? (
            <div className="grid grid-cols-2 gap-2 mb-4">
              <OpsMini icon={FolderOpen} label="Proyectos activos" value={String(ops.activeProjects)}
                       sub={ops.projectsThisMonth > 0 ? `+${ops.projectsThisMonth} este mes` : undefined} tone="blue" />
              <OpsMini icon={CheckSquare} label="Tareas abiertas" value={String(ops.openTasks)}
                       sub={ops.tasksDueSoon > 0 ? `${ops.tasksDueSoon} vencen en 7 días` : undefined} tone="amber" />
              <OpsMini icon={Clock} label="Partes por revisar" value={String(ops.submittedTimesheets)}
                       highlight={ops.submittedTimesheets > 0} tone="red" />
              <OpsMini icon={CreditCard} label="Facturación mes" value={`${ops.monthAmount.toLocaleString('es-ES')} €`}
                       sub={ops.pendingPayments > 0 ? `${ops.pendingPayments} pagos pendientes` : undefined} tone="emerald" />
            </div>
          ) : (
            <div className="text-sm text-gray-400 mb-4">Cargando…</div>
          )}

          {ops && ops.recentProjects.length > 0 && (
            <div>
              <p className="text-xs uppercase font-medium text-gray-500 mb-2">Últimos proyectos</p>
              <ul className="space-y-1.5">
                {ops.recentProjects.map(p => (
                  <li key={p.id} className="flex items-center justify-between text-sm">
                    <div className="min-w-0 flex-1 truncate">
                      <p className="text-gray-900 truncate">{p.name}</p>
                      <p className="text-xs text-gray-500 truncate">{p.client_name || 'Sin cliente'}</p>
                    </div>
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${
                      p.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      p.status === 'completed' ? 'bg-gray-100 text-gray-600' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {p.status === 'active' ? 'Activo' : p.status === 'completed' ? 'Completado' : 'Pausado'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Fila 2: Actividades | Reuniones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {userIsAdmin ? 'Actividades Recientes (Todas)' : 'Mis Actividades Recientes'}
            </h2>
          </div>
          <div className="p-6">
            {loadingActivities ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-gray-500">Cargando actividades...</p>
              </div>
            ) : activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map((activity) => {
                  const Icon = getActivityIcon(activity.type);
                  const colorClass = getActivityColor(activity.type);
                  const activityDate = activity.datetime ? new Date(activity.datetime) : new Date();
                  const createdDate = activity.createdAt ? new Date(activity.createdAt) : activityDate;
                  const description = truncateText(activity.description);
                  const relatedName = activity.contactName || activity.leadName || 'Sin asignar';

                  return (
                    <div key={activity.id} className="flex items-start space-x-3 group">
                      <div className={`p-2 rounded-lg ${colorClass} flex-shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-sm text-gray-900 mb-1"
                          title={activity.description}
                        >
                          {description}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                          <span className="font-medium">{relatedName}</span>
                          <span>•</span>
                          <span>{formatDate(createdDate)}</span>
                          <span>•</span>
                          <span>{formatTime(createdDate)}</span>
                          {activity.owner && (
                            <>
                              <span>•</span>
                              <span className="text-blue-600">{activity.owner}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">📋</div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">Sin actividades recientes</h4>
                <p className="text-gray-600 text-sm">
                  {userIsAdmin
                    ? 'Las actividades de todos los usuarios aparecerán aquí'
                    : 'Tus actividades aparecerán aquí cuando interactúes con contactos'}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {userIsAdmin ? 'Próximas Reuniones (Todas)' : 'Mis Próximas Reuniones'}
              </h2>
              {loadingMeetings && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              )}
            </div>
          </div>
          <div className="p-6">
            {loadingMeetings ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-gray-500">Cargando reuniones...</p>
              </div>
            ) : userMeetings.length > 0 ? (
              <div className="space-y-4">
                {userMeetings.map((meeting) => {
                  const contact = contacts.find(c => c.id === meeting.contactId);

                  const getStatusBadge = () => {
                    return (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        Confirmada
                      </span>
                    );
                  };

                  const isFromGoogle = meeting.google_event_id || meeting.sync_source === 'google';

                  return (
                    <div key={meeting.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${isFromGoogle ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                          {isFromGoogle ? (
                            <Cloud className="w-4 h-4" />
                          ) : (
                            <Calendar className="w-4 h-4" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900">{meeting.title}</p>
                            {isFromGoogle && (
                              <span className="inline-flex items-center space-x-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                                <Download className="w-3 h-3" />
                                <span>Google</span>
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {contact?.fullName || meeting.clientName || 'Cliente'} • {meeting.datetime.toLocaleDateString()} {meeting.datetime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </p>
                          {meeting.clientEmail && (
                            <p className="text-xs text-gray-400">
                              {meeting.clientEmail}
                            </p>
                          )}
                          {isFromGoogle && meeting.participants && meeting.participants.length > 0 && (
                            <p className="text-xs text-blue-600 mt-1">
                              Con: {meeting.participants.slice(0, 2).join(', ')}
                              {meeting.participants.length > 2 && ` +${meeting.participants.length - 2} más`}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {getStatusBadge()}
                        {meeting.assignedUserId === currentUser?.id && (
                          <p className="text-xs text-gray-500 mt-1">
                            Tu reunión
                          </p>
                        )}
                        {userIsAdmin && meeting.assignedUserId !== currentUser?.id && (
                          <p className="text-xs text-gray-500 mt-1">
                            Asignada a {meetings.find(m => m.id === meeting.id)?.owner || 'otro usuario'}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {userIsAdmin ? 'No hay reuniones aprobadas próximas' : 'No tienes reuniones confirmadas próximas'}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {userIsAdmin
                    ? 'Las reuniones aprobadas aparecerán aquí cuando se confirmen'
                    : 'Tus reuniones confirmadas aparecerán aquí'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agent Calls Widget */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <AgentCallsList
          calls={recentAgentCalls}
          isLoading={recentCallsLoading}
          title="Últimas llamadas del asistente de Cero Ideas"
          onRowClick={(call) => {
            setSelectedCall(call);
            setCallModalOpen(true);
          }}
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Pipeline de Ventas</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { status: 'nuevo', label: 'Nuevos', count: pipelineStats.nuevo || 0, color: 'bg-blue-100 text-blue-800' },
              { status: 'en_seguimiento', label: 'En Seguimiento', count: pipelineStats.en_seguimiento || 0, color: 'bg-yellow-100 text-yellow-800' },
              { status: 'cerrado', label: 'Cerrados', count: pipelineStats.cerrado || 0, color: 'bg-green-100 text-green-800' },
              { status: 'descartado', label: 'Descartados', count: pipelineStats.descartado || 0, color: 'bg-red-100 text-red-800' },
              { status: 'ya_es_cliente', label: 'Ya son Clientes', count: pipelineStats.ya_es_cliente || 0, color: 'bg-purple-100 text-purple-800' }
            ].map((stage) => (
              <div key={stage.status} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${stage.color} mb-2`}>
                  {stage.count}
                </div>
                <p className="text-sm font-medium text-gray-900">{stage.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Agent Call Detail Modal */}
      <AgentCallDetailModal
        call={selectedCall}
        open={callModalOpen}
        onClose={() => setCallModalOpen(false)}
      />
    </div>
  );
};

const OpsMini: React.FC<{
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  tone?: 'blue' | 'amber' | 'red' | 'emerald';
  highlight?: boolean;
}> = ({ icon: Icon, label, value, sub, tone = 'blue', highlight }) => {
  const tones = {
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    iconBg: 'bg-blue-100'    },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   iconBg: 'bg-amber-100'   },
    red:     { bg: 'bg-red-50',     text: 'text-red-700',     iconBg: 'bg-red-100'     },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', iconBg: 'bg-emerald-100' },
  }[tone];
  return (
    <div className={`rounded-lg border p-3 ${highlight ? 'border-red-300 ' + tones.bg : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] uppercase font-medium text-gray-500 leading-tight">{label}</p>
          <p className={`text-lg font-bold mt-0.5 ${highlight ? tones.text : 'text-gray-900'}`}>{value}</p>
          {sub && <p className="text-[11px] text-gray-500 mt-0.5 truncate">{sub}</p>}
        </div>
        <div className={`p-1.5 rounded ${tones.iconBg} flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${tones.text}`} />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
